import React, { useState, useEffect, useMemo } from "react";
import AdminModal from "../../Common/AdminModal";
import { getQuestionsList } from "../../../../services/adminService";
import { Search, Check, Filter } from "lucide-react";

export default function QuestionPickerModal({
  isOpen,
  onClose,
  onAddQuestions,
  alreadySelectedIds = [],
}) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("All");
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      getQuestionsList()
        .then((data) => {
          setQuestions(data);
          setSelectedIds([]);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  const subjects = useMemo(() => {
    const s = new Set();
    questions.forEach((q) => s.add(q.subject));
    return ["All", ...Array.from(s)];
  }, [questions]);

  const filteredQuestions = useMemo(() => {
    return questions.filter((q) => {
      if (alreadySelectedIds.includes(q.id)) return false;
      if (subjectFilter !== "All" && q.subject !== subjectFilter) return false;
      if (search.trim()) {
        const t = search.toLowerCase();
        return (
          q.question.toLowerCase().includes(t) ||
          q.id.toLowerCase().includes(t) ||
          (q.topic && q.topic.toLowerCase().includes(t))
        );
      }
      return true;
    });
  }, [questions, alreadySelectedIds, subjectFilter, search]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleConfirm = () => {
    onAddQuestions(selectedIds);
    onClose();
  };

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Questions from Question Bank"
      maxWidth="840px"
      footer={
        <>
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            disabled={selectedIds.length === 0}
            onClick={handleConfirm}
          >
            <Check size={16} /> Add {selectedIds.length} Selected Questions
          </button>
        </>
      }
    >
      <div>
        <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 14px 0" }}>
          Select existing questions from the central bank. No duplicate question records will be created.
        </p>

        {/* Search & Filter Toolbar */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
          <div className="admin-search-input-wrap" style={{ flex: 1 }}>
            <Search size={16} className="admin-search-icon" />
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search by keyword, topic, or question ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Filter size={15} style={{ color: "#64748b" }} />
            <select
              className="admin-form-select"
              style={{ width: "150px" }}
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
            >
              {subjects.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Question Select List */}
        <div
          style={{
            maxHeight: "360px",
            overflowY: "auto",
            border: "1px solid var(--admin-border)",
            borderRadius: "8px",
          }}
        >
          {loading ? (
            <div style={{ padding: "30px", textAlign: "center", color: "#64748b" }}>
              Loading Question Bank...
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div style={{ padding: "30px", textAlign: "center", color: "#64748b" }}>
              No available questions found matching filters.
            </div>
          ) : (
            filteredQuestions.map((q) => {
              const isSelected = selectedIds.includes(q.id);
              return (
                <div
                  key={q.id}
                  onClick={() => toggleSelect(q.id)}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid var(--admin-border)",
                    cursor: "pointer",
                    background: isSelected ? "#f0fdf4" : "#ffffff",
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                    transition: "background 0.15s ease",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    style={{ marginTop: "4px", cursor: "pointer" }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "4px" }}>
                      <span style={{ fontWeight: 700, fontSize: "12px", color: "#0284c7" }}>
                        {q.id}
                      </span>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          background: "#f1f5f9",
                          padding: "1px 6px",
                          borderRadius: "4px",
                          color: "#475569",
                        }}
                      >
                        {q.subject} &bull; {q.topic}
                      </span>
                      <span className={`status-badge ${(q.difficulty || "").toLowerCase()}`} style={{ fontSize: "10px" }}>
                        {q.difficulty}
                      </span>
                      {q.pyq_year && (
                        <span style={{ fontSize: "11px", color: "#64748b" }}>PYQ {q.pyq_year}</span>
                      )}
                    </div>
                    <p style={{ margin: 0, fontSize: "13px", color: "#334155", lineHeight: "1.4" }}>
                      {q.question}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </AdminModal>
  );
}
